'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { Button } from '@/shared/components/Button';
import VideoCarousel from '@/shared/components/VideoCarousel';
import { SupportForm } from '@/app/(support)/components/SupportForm';
import { useAnalytics } from '@/core/analytics';
import { logger } from '@/core/logger';

interface Article {
	id: string;
	title: string;
	slug: string;
	category: string;
	content: string;
	views: number;
}

// Helper to strip HTML tags for preview
function stripHtml(html: string): string {
	return html.replace(/<[^>]*>/g, '');
}

export default function HelpPage() {
	const [showSupportForm, setShowSupportForm] = useState(false);
	const [articles, setArticles] = useState<Article[]>([]);
	const [searchQuery, setSearchQuery] = useState('');
	const [searchResults, setSearchResults] = useState<Article[]>([]);
	const [isSearching, setIsSearching] = useState(false);
	const [loading, setLoading] = useState(true);
	const analytics = useAnalytics();

	// Track page visit and fetch articles on mount
	useEffect(() => {
		analytics.logEvent('help_center_visited');

		const fetchArticles = async () => {
			try {
				const response = await fetch('/api/knowledge-base/articles');
				const data = await response.json();
				if (data.success) {
					setArticles(data.articles);
				}
			} catch (error) {
				logger.error('Error fetching articles', error);
			} finally {
				setLoading(false);
			}
		};

		fetchArticles();
	}, []);

	// Handle search
	useEffect(() => {
		if (searchQuery.trim().length === 0) {
			setSearchResults([]);
			setIsSearching(false);
			return;
		}

		const searchArticles = async () => {
			setIsSearching(true);
			try {
				const response = await fetch(
					`/api/knowledge-base/search?q=${encodeURIComponent(searchQuery)}`
				);
				const data = await response.json();
				if (data.success) {
					setSearchResults(data.articles);
				}
			} catch (error) {
				logger.error('Error searching articles', error);
			} finally {
				setIsSearching(false);
			}
		};

		const debounce = setTimeout(searchArticles, 300);
		return () => clearTimeout(debounce);
	}, [searchQuery]);

	// Get top 3 popular articles
	const popularArticles = articles
		.sort((a, b) => b.views - a.views)
		.slice(0, 3);

	// Group articles by category
	const articlesByCategory = articles.reduce(
		(acc, article) => {
			if (!acc[article.category]) {
				acc[article.category] = [];
			}
			acc[article.category].push(article);
			return acc;
		},
		{} as Record<string, Article[]>
	);
	return (
		<div className="min-h-screen overflow-hidden">
			{/* Fullscreen Video Background */}
			<div className="fixed inset-0 z-0">
				<VideoCarousel />
			</div>

			{/* Navigation */}
			<nav className="glass-dark fixed top-0 left-0 right-0 z-50 border-b border-white/10">
				<div className="container mx-auto px-4 py-4 flex justify-between items-center">
					<Link href="/" className="flex items-center">
						<Image
							src="/ts-logo2-v2.png"
							alt="TabStudio Logo"
							width={160}
							height={53}
							className="h-12 w-auto"
							loading="lazy"
							unoptimized
						/>
					</Link>
					<div className="flex items-center gap-4">
						<Link href="/login" prefetch={false}>
							<Button
								variant="ghost"
								size="sm"
								className="glass-light glass-hover text-white border-white/20"
							>
								Sign In
							</Button>
						</Link>
						<Link href="/register" prefetch={false}>
							<Button
								variant="default"
								size="sm"
								className="glass-frosted glass-hover bg-white/90 text-black font-semibold border-white/30"
							>
								Get Started
							</Button>
						</Link>
					</div>
				</div>
			</nav>

			<main className="relative z-10 pt-24">
				{/* Hero Section */}
				<section className="py-24 px-4">
					<div className="container mx-auto text-center">
						<h1 className="text-5xl md:text-7xl font-bold mb-6">
							Help
							<br />
							<span className="bg-gradient-to-r from-red-500 to-red-700 bg-clip-text text-transparent">
								Center
							</span>
						</h1>
						<p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-8">
							Get help, find answers, and learn how to make the
							most of TabStudio.
						</p>
						<div className="max-w-2xl mx-auto relative">
							<input
								type="search"
								placeholder="Search for help..."
								value={searchQuery}
								onChange={(e) => setSearchQuery(e.target.value)}
								className="w-full px-6 py-4 rounded-lg bg-background/50 border border-white/10 bg-white/5 backdrop-blur-sm focus:border-primary/50 focus:outline-none text-white"
							/>
							{isSearching && (
								<div className="absolute right-4 top-1/2 -translate-y-1/2">
									<div className="w-5 h-5 border-2 border-white/20 border-t-white rounded-full animate-spin" />
								</div>
							)}
							{searchResults.length > 0 && (
								<div className="absolute top-full mt-2 w-full bg-black/90 backdrop-blur-xl border border-white/20 rounded-lg shadow-2xl max-h-96 overflow-y-auto z-50">
									{searchResults.map((article) => (
										<Link
											key={article.id}
											href={`/help/${article.slug}`}
											className="block p-4 hover:bg-white/10 transition-colors border-b border-white/10 last:border-b-0"
										>
											<div className="flex items-start justify-between">
												<div>
													<h4 className="font-semibold text-white mb-1">
														{article.title}
													</h4>
													<p className="text-sm text-white/60 line-clamp-2">
														{stripHtml(
															article.content
														)}
													</p>
												</div>
												<span className="text-xs text-white/40 ml-4 flex-shrink-0">
													{article.category}
												</span>
											</div>
										</Link>
									))}
								</div>
							)}
						</div>
					</div>
				</section>

				{/* Popular Articles from Notion */}
				<section className="py-16 px-4 bg-card/30 backdrop-blur-sm">
					<div className="container mx-auto max-w-6xl">
						<h2 className="text-3xl md:text-4xl font-bold mb-12 text-center">
							Popular Articles
						</h2>
						{loading ? (
							<div className="flex justify-center items-center py-12">
								<div className="w-12 h-12 border-3 border-white/20 border-t-white rounded-full animate-spin" />
							</div>
						) : popularArticles.length > 0 ? (
							<div className="grid gap-8 md:grid-cols-3">
								{popularArticles.map((article) => (
									<Link
										key={article.id}
										href={`/help/${article.slug}`}
										className="p-8 rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm hover:border-primary/50 transition-all group"
									>
										<div className="flex items-start justify-between mb-4">
											<span className="text-xs font-medium text-primary bg-primary/10 px-3 py-1 rounded-full">
												{article.category}
											</span>
											<span className="text-xs text-white/40">
												{article.views} views
											</span>
										</div>
										<h3 className="text-xl font-semibold mb-3 group-hover:text-primary transition-colors">
											{article.title}
										</h3>
										<p className="text-muted-foreground line-clamp-3">
											{stripHtml(article.content)}
										</p>
									</Link>
								))}
							</div>
						) : (
							<div className="text-center text-white/60 py-12">
								<p>
									No articles available yet. Check back soon!
								</p>
							</div>
						)}
					</div>
				</section>

				{/* Articles by Category */}
				{!loading && Object.keys(articlesByCategory).length > 0 && (
					<section className="py-16 px-4">
						<div className="container mx-auto max-w-6xl">
							<h2 className="text-3xl md:text-4xl font-bold mb-12 text-center">
								Browse by Category
							</h2>
							<div className="space-y-12">
								{Object.entries(articlesByCategory).map(
									([category, categoryArticles]) => (
										<div key={category}>
											<h3 className="text-2xl font-bold mb-6">
												{category}
											</h3>
											<div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
												{categoryArticles.map(
													(article) => (
														<Link
															key={article.id}
															href={`/help/${article.slug}`}
															className="p-6 rounded-xl border border-white/10 bg-white/5 backdrop-blur-sm hover:border-primary/50 transition-all group"
														>
															<h4 className="font-semibold mb-2 group-hover:text-primary transition-colors">
																{article.title}
															</h4>
															<p className="text-sm text-muted-foreground line-clamp-2">
																{stripHtml(
																	article.content
																)}
															</p>
															<div className="flex items-center justify-between mt-3">
																<span className="text-xs text-white/40">
																	{
																		article.views
																	}{' '}
																	views
																</span>
															</div>
														</Link>
													)
												)}
											</div>
										</div>
									)
								)}
							</div>
						</div>
					</section>
				)}

				{/* Support Options */}
				<section className="py-16 px-4 bg-card/30 backdrop-blur-sm">
					<div className="container mx-auto">
						<h2 className="text-3xl md:text-4xl font-bold mb-12 text-center">
							Get in Touch
						</h2>
						<div className="grid gap-8 md:grid-cols-2 max-w-3xl mx-auto">
							<div className="p-8 rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm text-center">
								<div className="w-12 h-12 mx-auto mb-6 bg-gradient-to-br from-secondary to-secondary-dark rounded-xl flex items-center justify-center">
									<svg
										className="w-6 h-6 text-white"
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24"
									>
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth={2}
											d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z"
										/>
									</svg>
								</div>
								<h3 className="text-xl font-semibold mb-3">
									Create Support Ticket
								</h3>
								<p className="text-muted-foreground mb-4">
									Submit a ticket and our team will get back
									to you soon.
								</p>
								<Button
									onClick={() => setShowSupportForm(true)}
									variant="outline"
									className="glass-light glass-hover border-2 border-white/30 text-white font-semibold"
								>
									Submit Ticket
								</Button>
							</div>
							<div className="p-8 rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm text-center">
								<div className="w-12 h-12 mx-auto mb-6 bg-gradient-to-br from-primary to-primary-dark rounded-xl flex items-center justify-center">
									<svg
										className="w-6 h-6 text-white"
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24"
									>
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth={2}
											d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
										/>
									</svg>
								</div>
								<h3 className="text-xl font-semibold mb-3">
									Email Support
								</h3>
								<p className="text-muted-foreground mb-4">
									Get help from our support team within 24
									hours.
								</p>
								<a
									href="mailto:support@tabspace.ai"
									className="text-primary hover:underline"
								>
									support@tabspace.ai
								</a>
							</div>
						</div>
					</div>
				</section>

				{/* CTA Section */}
				<section className="py-24 px-4">
					<div className="container mx-auto">
						<div className="glass-gradient glass-hover relative max-w-4xl mx-auto text-center p-12 rounded-3xl border border-white/20 overflow-hidden shadow-2xl">
							<div className="relative z-10">
								<h2 className="text-4xl md:text-5xl font-bold mb-6">
									Still Need Help?
								</h2>
								<p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
									Our support team is ready to assist you with
									any questions or issues.
								</p>
								<Button
									onClick={() => setShowSupportForm(true)}
									variant="default"
									size="lg"
									className="glass-frosted glass-hover bg-white/90 text-black font-semibold border-white/30"
								>
									Contact Support
								</Button>
							</div>
						</div>
					</div>
				</section>
			</main>

			{/* Footer */}
			<footer className="border-t border-border/50 bg-card/30 backdrop-blur-sm">
				<div className="container mx-auto px-4 py-12">
					<div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-8">
						<div>
							<h4 className="font-semibold mb-4">Product</h4>
							<ul className="space-y-2 text-sm text-muted-foreground">
								<li>
									<Link href="/features">Features</Link>
								</li>
							</ul>
						</div>
						<div>
							<h4 className="font-semibold mb-4">Company</h4>
							<ul className="space-y-2 text-sm text-muted-foreground">
								<li>
									<Link href="/about">About</Link>
								</li>
								<li>
									<Link href="/careers">Careers</Link>
								</li>
							</ul>
						</div>
						<div>
							<h4 className="font-semibold mb-4">Resources</h4>
							<ul className="space-y-2 text-sm text-muted-foreground">
								<li>
									<Link href="/help">Help Center</Link>
								</li>
								<li>
									<Link href="/api">API</Link>
								</li>
							</ul>
						</div>
						<div>
							<h4 className="font-semibold mb-4">Legal</h4>
							<ul className="space-y-2 text-sm text-muted-foreground">
								<li>
									<Link href="/privacy">Privacy</Link>
								</li>
								<li>
									<Link href="/terms">Terms</Link>
								</li>
								<li>
									<Link href="/security">Security</Link>
								</li>
							</ul>
						</div>
					</div>
					<div className="pt-8 border-t border-border/50 text-center text-sm text-muted-foreground">
						<p>
							&copy; 2025 TabStudio. Powered by AI. Built for
							creators.
						</p>
					</div>
				</div>
			</footer>

			{/* Support Form Modal */}
			{showSupportForm && (
				<div
					className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-md"
					onClick={() => setShowSupportForm(false)}
				>
					<div
						className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-white/5 backdrop-blur-xl border border-white/20 rounded-2xl p-8 shadow-2xl scrollbar-hide"
						onClick={(e) => e.stopPropagation()}
					>
						<button
							onClick={() => setShowSupportForm(false)}
							className="absolute top-4 right-4 p-2 hover:bg-white/10 rounded-lg transition-colors"
							aria-label="Close"
						>
							<svg
								className="w-6 h-6"
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24"
							>
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									strokeWidth={2}
									d="M6 18L18 6M6 6l12 12"
								/>
							</svg>
						</button>
						<h2 className="text-2xl font-bold mb-6">
							Submit a Support Ticket
						</h2>
						<SupportForm
							onClose={() => setShowSupportForm(false)}
						/>
					</div>
				</div>
			)}
		</div>
	);
}
